import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import { useFabricEditor } from 'modules/fabric-editor';
import { selectActiveBoard } from 'modules/manager/store';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import BoardInfo from './board-info';
import { FallbackInspector, getInspector } from './inspectors';
import LayerSection from './inspectors/common/layer-section';
import LayoutBlocksPanel from './layout-blocks-panel';

const EditorRightToolbar: React.FC = () => {
  const editor = useFabricEditor();
  const board = useAppSelector(selectActiveBoard);
  const [active, setActive] = useState<fabric.Object[]>([]);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const offSel = editor.on('selection:changed', (e) => setActive(e.active));
    const offZoom = editor.on('zoom:changed', (e) => setZoom(e.zoom));
    return () => {
      offSel();
      offZoom();
    };
  }, [editor]);

  return (
    <Root>
      <SectionTitle>Inspector</SectionTitle>
      {renderBody()}
    </Root>
  );

  function renderBody() {
    if (active.length === 0) {
      return (
        <NoSelection>
          <BoardInfo
            name={board?.name}
            version={board?.version}
            objectCount={board?.objects?.length ?? 0}
            zoom={zoom}
          />
          <LayoutBlocksPanel editor={editor} />
        </NoSelection>
      );
    }
    if (active.length > 1) {
      return (
        <MultiSelection>
          <Row>
            <Label>Selected</Label>
            <Value>{active.length}</Value>
          </Row>
          <Hint>Multi-object editing is not supported yet.</Hint>
        </MultiSelection>
      );
    }
    const obj = active[0];
    const dataKind = (obj.data as { kind?: string } | undefined)?.kind;
    const Inspector = getInspector(dataKind ?? obj.type) ?? FallbackInspector;
    return (
      <InspectorBody>
        <Row>
          <Label>Type</Label>
          <Value>{dataKind ?? obj.type ?? 'unknown'}</Value>
        </Row>
        <Inspector object={obj} editor={editor} />
        <LayerSection object={obj} editor={editor} />
      </InspectorBody>
    );
  }
};

export default React.memo(EditorRightToolbar);

const Root = styled.div`
  width: 280px;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 1px solid #e5e7eb;
  background: #ffffff;
  overflow-y: auto;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 0;
`;

const InspectorBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MultiSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
`;

const Label = styled.span`
  color: #6b7280;
`;

const Value = styled.span`
  color: #111827;
  font-weight: 500;
`;

const Hint = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
`;
